<template>
    <div 
        class="menu-item-grid" 
        :style="{ '--nutrition-columns': nutritionColumns.length }"
        role="table"
        aria-label="Menu items nutrition information"
        :aria-rowcount="flattenedRows.length + 1"
        :aria-colcount="totalColumns"
        @keydown="handleKeyboardNavigation"
        @blur="handleFocusLoss"
        @focus="handleTableFocus"
        tabindex="0"
    >
        <button 
            :class="['grid-header', 'first-column-header', 'app-sticky-column', 'sortable', { 'selected': hasFocus && selectedRow === -1 && selectedCol === 0 }]" 
            @click="handleSort('DisplayName')"
            :aria-sort="getSortAttribute('DisplayName')"
            aria-label="Sort by menu item name and allergens"
            :aria-colindex="1"
            role="columnheader"
            :tabindex="(hasFocus && selectedRow === -1 && selectedCol === 0) ? '0' : '-1'"
        >
            <div class="sort-header">
                <div class="header-text-row">
                    <div class="menu-item-with-sort">
                        <multi-line-label text="Menu Item" />
                        <div class="sort-indicator">
                            <i v-if="sortColumn === 'DisplayName'" :class="['fa-solid', sortDirection === 'asc' ? 'fa-sort-up' : 'fa-sort-down']" aria-hidden="true"></i>
                            <i v-else class="fa-solid fa-sort" aria-hidden="true"></i>
                        </div>
                    </div>
                </div>
            </div>
        </button>
        <button 
            :class="['grid-header', 'sortable', { 'selected': hasFocus && selectedRow === -1 && selectedCol === 1 }]" 
            @click="handleSort('ServingDisplay')"
            :aria-sort="getSortAttribute('ServingDisplay')"
            aria-label="Sort by serving size"
            :aria-colindex="2"
            role="columnheader"
            :tabindex="(hasFocus && selectedRow === -1 && selectedCol === 1) ? '0' : '-1'"
        >
            <div class="sort-header">
                <multi-line-label text="Serving Size" />
                <div class="sort-indicator">
                    <i v-if="sortColumn === 'ServingDisplay'" :class="['fa-solid', sortDirection === 'asc' ? 'fa-sort-up' : 'fa-sort-down']" aria-hidden="true"></i>
                    <i v-else class="fa-solid fa-sort" aria-hidden="true"></i>
                </div>
            </div>
        </button>
        <div 
            :class="['grid-header', { 'selected': hasFocus && selectedRow === -1 && selectedCol === 2 }]" 
            aria-label="Allergens column"
            :aria-colindex="3"
            role="columnheader"
            :tabindex="(hasFocus && selectedRow === -1 && selectedCol === 2) ? '0' : '-1'"
            @blur="handleFocusLoss"
        >
            <span>Allergens</span>
        </div>
        <button 
            v-for="(column, index) in nutritionColumns" 
            :key="column.NutritionId" 
            :class="['grid-header', 'sortable', { 'selected': hasFocus && selectedRow === -1 && selectedCol === index + 3 }]" 
            @click="handleSort(column.NutritionComponentId)"
            :aria-sort="getSortAttribute(column.NutritionComponentId)"
            :aria-label="`Sort by ${column.ShortName}`"
            :aria-colindex="index + 4"
            role="columnheader"
            :tabindex="(hasFocus && selectedRow === -1 && selectedCol === index + 3) ? '0' : '-1'"
        >
            <div class="sort-header">
                <multi-line-label :text="column.ShortName" />
                <div class="sort-indicator">
                    <i v-if="sortColumn === column.NutritionComponentId" :class="['fa-solid', sortDirection === 'asc' ? 'fa-sort-up' : 'fa-sort-down']" aria-hidden="true"></i>
                    <i v-else class="fa-solid fa-sort" aria-hidden="true"></i>
                </div>
            </div>
        </button>
        
        <template v-if="flattenedRows.length > 0">
            <menu-item-row 
                v-for="(rowData, index) in flattenedRows" 
                :key="`${rowData.item.MenuItemId}-${rowData.isChild ? `child-${rowData.childIndex}` : 'parent'}-${index}`"
                :item="rowData.item"
                :row-data="rowData"
                :row-index="index"
                :selection-context="selectionContext"
                :children-visible="childVisibilityState[rowData.item.MenuItemId] || false"
                @cell-click="onCellClick"
                @cell-blur="handleFocusLoss"
                @toggle-children="toggleChildVisibility(rowData.item.MenuItemId)"
                ref="menuItemRows"
            />
        </template>
        <div v-else class="no-results-row" :style="{ gridColumn: `1 / span ${totalColumns}` }" role="status">
            <span>No results found.</span>
        </div>
    </div>
</template>

<script>
import multiLineLabel from '../../components/multiLineLabel.vue';
import adaMixin from '../../js/mixins/adaMixin';
import menuItemRow from './menuItemRow.vue';

export default {
    name: 'menu-item-table',
    mixins: [adaMixin],
    components: {
        'menu-item-row': menuItemRow,
        'multi-line-label': multiLineLabel
    },
    props: {
        items: {
            type: Array,
            required: true
        },
        sortColumn: {
            type: [String, Number],
            default: null
        },
        sortDirection: {
            type: String,
            default: 'asc'
        }
    },
    data() {
        return {
            selectedRow: -1,
            selectedCol: -1,
            totalColumns: 0,
            childVisibilityState: {},
            hasFocus: false,
            BASE_COLUMNS_COUNT: 3
        };
    },
    computed: {
        nutritionColumns() {
            return this.$store.state.PublicMenuModule.nutrition;
        },
        selectionContext() {
            return {
                selectedRow: this.selectedRow,
                selectedCol: this.selectedCol,
                hasFocus: this.hasFocus,
                totalRows: this.flattenedRows.length,
                totalColumns: this.totalColumns,
                ariaRowOffset: 2
            };
        },
        columnHeaders() {
            return [
                { id: 'DisplayName', label: 'Menu Item' },
                { id: 'ServingDisplay', label: 'Serving' },
                { id: 'Allergens', label: 'Allergens' },
                ...this.nutritionColumns.map(col => ({
                    id: col.NutritionComponentId,
                    label: col.ShortName
                }))
            ];
        },
        flattenedRows() {
            const rows = [];
            this.items.forEach((item, index) => {
                const hasChildren = item.ChildItems && item.ChildItems.length > 0;
                
                rows.push({ 
                    item, 
                    parentIndex: index, 
                    isChild: false, 
                    childIndex: -1,
                    hasChildren
                });
                
                if (hasChildren && this.childVisibilityState[item.MenuItemId] !== false) {
                    item.ChildItems.forEach((child, childIndex) => {
                        rows.push({
                            item: child,
                            parentIndex: index,
                            isChild: true,
                            childIndex,
                            hasChildren: false
                        });
                    });
                }
            });
            return rows;
        }
    },
    mounted() {
        this.totalColumns = this.BASE_COLUMNS_COUNT + this.nutritionColumns.length;
        this.initializeChildVisibility();
    },
    watch: {
        nutritionColumns() {
            this.totalColumns = this.BASE_COLUMNS_COUNT + this.nutritionColumns.length;
        },
        items() {
            this.initializeChildVisibility();
        }
    },
    methods: {
        handleSort(column) {
            this.$emit('sort', column);
        },
        handleKeyboardNavigation(event) {
            const { key } = event;
            const totalRows = this.flattenedRows.length;
            
            if (key === 'Tab') {
                return;
            }
            
            if (totalRows === 0) return;
            
            event.preventDefault();
            
            switch(key) {
                case 'ArrowUp':
                    if (this.selectedRow > 0) {
                        this.selectedRow--;
                    } else if (this.selectedRow === 0) {
                        this.selectedRow = -1;
                    }
                    break;
                    
                case 'ArrowDown':
                    if (this.selectedRow < totalRows - 1) {
                        this.selectedRow++;
                    }
                    break;
                    
                case 'ArrowLeft':
                    if (this.selectedCol > 0) {
                        this.selectedCol--;
                    }
                    break;
                    
                case 'ArrowRight':
                    if (this.selectedCol < this.totalColumns - 1) {
                        this.selectedCol++;
                    }
                    break;
                    
                case 'Home':
                    this.selectedRow = 0;
                    this.selectedCol = 0;
                    break;
                    
                case 'End':
                    this.selectedRow = totalRows - 1;
                    this.selectedCol = this.totalColumns - 1;
                    break;
                    
                case 'Enter':
                case ' ':
                    if (this.selectedRow === -1 && this.selectedCol >= 0) {
                        this.handleSort(this.columnHeaders[this.selectedCol].id);
                    } else if (this.selectedRow >= 0) {
                        const rowData = this.flattenedRows[this.selectedRow];
                        if (rowData && rowData.hasChildren) {
                            this.toggleChildVisibility(rowData.item.MenuItemId);
                        }
                    }
                    //Don't refocus
                    return
                
                case 'Escape':
                    this.hasFocus = false;
                    this.clearSelection();
                    this.$forceUpdate();
                    return;
            }
            this.updateCellFocus();
        },
        updateCellFocus() {
            this.$forceUpdate();
            
            this.$nextTick(() => {
                if (this.selectedRow === -1 && this.selectedCol >= 0) {
                    const headers = this.$el.querySelectorAll('.grid-header');
                    if (headers[this.selectedCol]) {
                        headers[this.selectedCol].focus();
                    }
                } else if (this.selectedRow >= 0 && this.selectedCol >= 0) {
                    const dataCells = this.$el.querySelectorAll('.grid-cell');
                    const cellIndex = this.selectedRow * this.totalColumns + this.selectedCol;
                    if (dataCells[cellIndex]) {
                        dataCells[cellIndex].focus();
                        dataCells[cellIndex].scrollIntoView({ block: 'nearest', inline: 'nearest' });
                    }
                }
            });
        },
        onCellClick(row, col) {
            this.hasFocus = true;
            this.selectedRow = row;
            this.selectedCol = col;
            this.updateCellFocus();
        },
        handleFocusLoss() {
            this.$nextTick(() => {
                const activeElement = document.activeElement;
                const isWithinTable = this.$el.contains(activeElement);
                
                if (!isWithinTable) {
                    this.hasFocus = false;
                    this.clearSelection();
                    this.$forceUpdate();
                }
            });
        },
        handleTableFocus() {
            const shouldAutoSelect = !this.hasFocus;
            this.hasFocus = true;
            
            if (shouldAutoSelect && this.selectedRow === -1 && this.selectedCol === -1 && this.flattenedRows.length > 0) {
                this.selectedRow = 0;
                this.selectedCol = 0;
                this.updateCellFocus();
            }
        },
        clearSelection() {
            this.selectedRow = -1;
            this.selectedCol = -1;
            
            this.$forceUpdate();
            
            this.$nextTick(() => {
                if (this.$refs.menuItemRows) {
                    this.$refs.menuItemRows.forEach(row => {
                        if (row.$forceUpdate) {
                            row.$forceUpdate();
                        }
                    });
                }
            });
        },
        initializeChildVisibility() {
            this.childVisibilityState = this.items
                .filter(i => i.ChildItems?.length > 0)
                .reduce((acc, i) => ({ ...acc, [i.MenuItemId]: true }), {});
        },
        toggleChildVisibility(menuItemId) {
            this.childVisibilityState = {
                ...this.childVisibilityState,
                [menuItemId]: !this.childVisibilityState[menuItemId]
            };
        },
        getSortAttribute(column) {
            if (this.sortColumn === column) {
                return this.sortDirection === 'asc' ? 'ascending' : 'descending';
            }
            return 'none';
        },
    }
};
</script>

<style>
.menu-item-grid {
    font-size: 12px;
    width: 100%;
    max-height: calc(100vh - 500px);
    display: grid;
    grid-template-columns: minmax(300px, 2fr) minmax(120px, 1fr) minmax(150px, 1.2fr) repeat(var(--nutrition-columns), minmax(100px, 1fr));
    grid-template-rows: 45px;
    overflow-x: auto;
    overflow-y: auto;
}

.menu-item-grid::-webkit-scrollbar {
    height: 12px;
    width: 12px;
}

.menu-item-grid::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
}

.menu-item-grid::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
}

.menu-item-grid::-webkit-scrollbar-track,
.menu-item-grid::-webkit-scrollbar-thumb {
    border-radius: 3px;
}

.menu-item-grid::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.3);
}

.grid-header {
    text-align: right; 
    padding: 12px 16px;
    background-color: rgba(246, 246, 247, 1);
    white-space: normal;
    word-break: normal;
    hyphens: none;
    min-height: 45px;
    max-height: 45px;
    display: flex;
    align-items: center;
    border-bottom: 1px solid rgba(225, 225, 226, 1);
    position: sticky;
    top: 0;
    z-index: 3;
}

button.grid-header {
    border: none;
    font-family: inherit;
    font-size: inherit;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    outline: none;
    width: 100%;
    margin: 0;
    box-sizing: border-box;
}

button.grid-header.selected {
    outline: 2px solid #C2E66E !important;
}

div.grid-header {
    border: none;
    font-family: inherit;
    font-size: inherit;
    width: 100%;
    margin: 0;
    box-sizing: border-box;
    justify-content: flex-start;
    text-align: left;
}

.grid-header.first-column-header {
    border-top-left-radius: 16px;
}

.grid-header:last-of-type {
    border-top-right-radius: 16px;
}

.app-sticky-column {
    position: sticky;
    left: 0;
    top: 0;
    z-index: 10;
    background-color: rgba(246, 246, 247, 1);
}

.app-sticky-column::after {
    content: '';
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: 1px;
    background-color: rgba(225, 225, 226, 1);
}

.grid-header,
.sort-header span,
.sort-header div {
    color: rgba(138, 140, 144, 1);
    font-weight: 400;
}

.sortable {
    cursor: pointer;
    user-select: none;
}

.sortable:hover {
    background-color: rgba(235, 235, 236, 1);
}

.sort-header,
.header-text-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
}

.grid-header:not(.first-column-header) .sort-header {
    justify-content: flex-end;
    gap: 8px;
}

.sort-indicator,
.menu-item-with-sort {
    display: flex;
    align-items: center;
    gap: 8px;
}

.sort-indicator {
    margin-left: 8px;
    opacity: 0.6;
}

.sortable:hover .sort-indicator {
    opacity: 1;
}

.sort-indicator i {
    font-size: 12px;
}

.first-column-header {
    justify-content: space-between;
    text-align: left;
}

button.first-column-header {
    min-width: 300px;
    width: 100%;
}

@media screen and (max-width: 800px) {
    .menu-item-grid {
        grid-template-columns: minmax(200px, 1.5fr) minmax(100px, 1fr) minmax(120px, 1.2fr) repeat(var(--nutrition-columns), minmax(80px, 1fr));
    }
    
    .first-column-header,
    button.first-column-header {
        min-width: 200px;
        width: 100%;
    }
}

.menu-item-grid:focus-visible {
    outline: 2px solid #C2E66E;
    outline-offset: 2px;
}


.grid-header:focus,
.grid-header:focus-visible,
button.grid-header:focus,
button.grid-header:focus-visible {
    outline: none !important;
    box-shadow: none !important;
}


.selected,
button.grid-header.selected,
.grid-header.selected {
    background-color: rgba(194, 230, 110, 0.1) !important;
    outline: 2px solid #C2E66E !important;
    outline-offset: -2px !important;
    position: relative !important;
    z-index: 5 !important;
}

.no-results-row {
    padding: 40px 20px;
    text-align: center;
    color: rgba(138, 140, 144, 1);
    background-color: white;
    border-bottom: 1px solid rgba(225, 225, 226, 1);
    border-bottom-left-radius: 16px;
    border-bottom-right-radius: 16px;
    font-size: 18px;
}

.grid-header span,
.nutrition-column-header-text {
    white-space: normal;
    word-break: normal;
    hyphens: none;
    line-height: 1.2;
}
</style>